import { alpha, Avatar, SvgIconProps, SxProps } from '@mui/material';
import { isEmpty } from 'lodash';
import React from 'react';
import { useStyling } from '../../common/Theme';
import useLoadAttachment from '../../common/components/useLoadAttachment';
import { stringToColor } from '../../common/utils/themeUtils';

export const extractInitials = (str: string): string => {
    return isEmpty(str)
        ? ''
        : str.includes(' ')
          ? str
                .split(' ')
                .map((part) => part.substring(0, 1).toUpperCase())
                .join('')
          : str.substring(0, 2).toUpperCase();
};

export enum AvailableSizes {
    xs = 'xs',
    sm = 'sm',
    md = 'md',
    lg = 'lg'
}

export type AvatarSize = {
    width: string | number;
    height: string | number;
    fontSize: string | number;
};

export type AvatarSizesType = {
    [key in AvailableSizes]: AvatarSize;
};

export const AvatarSizes: AvatarSizesType = {
    xs: { width: 16, height: 16, fontSize: '8px' },
    sm: { width: 24, height: 24, fontSize: '12px' },
    md: { width: 48, height: 48, fontSize: '16px' },
    lg: { width: 64, height: 64, fontSize: '1.5rem' }
};

type RoundAvatarProps = {
    title?: string;
    Icon?: React.ComponentType<SvgIconProps>;
    iconSx?: SxProps;
    src?: any;
    bgColor?: string;
    imageId?: string;
    size?: AvatarSize;
    width?: number;
    height?: number;
    numOfLetters?: number;
    className?: string;
};

export default function RoundAvatar({
    imageId,
    title,
    size = AvatarSizes.sm,
    numOfLetters = 2,
    bgColor,
    Icon,
    iconSx = {},
    className
}: RoundAvatarProps) {
    const { theme } = useStyling();
    const { attachment, free } = useLoadAttachment(imageId);
    const hasPicture = !isEmpty(attachment);
    const bgCol = hasPicture ? alpha(theme.palette.common.black, 0) : (bgColor ?? stringToColor(title));
    const fgCol = theme.palette.getContrastText(bgCol);
    const sx: SxProps = {
        width: size.width,
        height: size.height,
        fontSize: size.fontSize,
        color: fgCol,
        bgcolor: bgCol
    };
    const initials = title ? extractInitials(title).substring(0, numOfLetters) : undefined;
    return (
        <Avatar alt={initials} src={attachment} sx={sx} className={className} onLoad={free}>
            {Icon ? <Icon sx={iconSx} /> : initials}
        </Avatar>
    );
}
